import React, { useState, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { PricingItem, CurrencyObject } from 'common/types';

import {
	Box,
	List,
	ListItem,
	ListItemText,
	Typography,
	Collapse,
	Radio,
	ListItemAvatar,
	Accordion,
	AccordionSummary,
	AccordionDetails,
} from '@mui/material';
import { Theme } from '@mui/material/styles';
import makeStyles from '@mui/styles/makeStyles';
import createStyles from '@mui/styles/createStyles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LinkIcon from '@mui/icons-material/Link';
import LinkOffIcon from '@mui/icons-material/LinkOff';
import MoneyTextField from 'common/components/MoneyTextField';
import EmptyState from 'common/components/EmptyState';
import PricingTableEmpty from './PricingTableEmpty';
import PricingTableEditModal from './PricingTableEditModal';
import PricingTableBody from './PricingTableBody';
import PricingTableSelectModal from './PricingTableSelectModal';
import { getItemsWithBasePrice } from 'views/Product/__utils/utils';
import { useTranslation } from 'services/localization/useTranslation';

import * as ShopSelectors from 'selectors/ShopSelectors';

type PricingTableType = PricingItem[] | string;

interface Props {
	pricingTable: PricingTableType;
	basePrice: number;
	currency: CurrencyObject;
	onBasePriceChange: (basePrice: number) => void;
	onTableChange: (basePrice: number, items: PricingItem[]) => void;
	onUseSavedTable: (tableName: string) => void;
	disableFixedPrice?: boolean;
	productId?: string;
	disabled?: boolean;
	disableEditingTemplates?: boolean;
}

const PricingTable = (props: Props) => {
	const {
		onBasePriceChange,
		onTableChange,
		onUseSavedTable,
		basePrice,
		currency,
		pricingTable,
		disableFixedPrice,
		disabled,
	} = props;
	const classes = useStyles();
	const { t } = useTranslation();
	const [isEditing, setEditing] = useState(false);
	const [isSelectingTable, setIsSelectingTable] = useState(false);
	const pricingTables = useSelector(ShopSelectors.activeShopPricingTables);

	const items: PricingItem[] = useMemo(() => {
		if (typeof pricingTable === 'string') {
			const table: PricingItem[] = pricingTables[pricingTable] ?? [];
			return getItemsWithBasePrice(table, basePrice);
		}
		return pricingTable;
	}, [pricingTable, pricingTables, basePrice]);
	const [isFixedPrice, setIsFixedPrice] = useState(items.length === 0);
	const displayAsFixedPrice = isFixedPrice && !disableFixedPrice;

	const handleUnlinkTable = () => {
		onTableChange(basePrice, items);
	};

	const handleSetFixedPrice = (isFixed: boolean) => {
		if (isFixedPrice === isFixed) return;
		if (isFixed && typeof pricingTable !== 'string' && items.length > 0) {
			const proceed = window.confirm(
				t(
					'pricing.fixedPriceWarning',
					'Setting a fixed price will clear your current pricing table. Do you want to continue?',
				),
			);
			if (!proceed) return;
		}
		setIsFixedPrice(isFixed);
		onTableChange(basePrice, []);
	};

	return (
		<Box>
			{!disableFixedPrice && (
				<>
					<List style={{ maxWidth: '400px' }}>
						<ListItem button onClick={() => handleSetFixedPrice(true)} disabled={disabled}>
							<ListItemAvatar>
								<Radio color="primary" checked={displayAsFixedPrice} disabled={disabled} />
							</ListItemAvatar>
							<ListItemText
								primary={t('pricing.fixedPrice', 'Fixed price')}
								secondary={t('pricing.fixedPriceDescription', 'Fixed price per order')}
							/>
						</ListItem>
					</List>
					<Collapse in={displayAsFixedPrice}>
						<Box
							p={2}
							display="flex"
							flexDirection="row"
							alignItems="center"
							justifyContent="space-between"
						>
							<MoneyTextField
								currency={currency}
								label={t('pricing.pricePerOrder', 'Price per order')}
								value={basePrice}
								useMinorUnits
								onValueChange={(newBasePrice) => {
									if (typeof pricingTable === 'string') {
										onBasePriceChange(newBasePrice);
									} else {
										onTableChange(newBasePrice, getItemsWithBasePrice(items, newBasePrice));
									}
								}}
								variant="outlined"
								disabled={disabled}
							/>
						</Box>
					</Collapse>
					<List style={{ maxWidth: '400px' }}>
						<ListItem button onClick={() => handleSetFixedPrice(false)} disabled={disabled}>
							<ListItemAvatar>
								<Radio color="primary" checked={!displayAsFixedPrice} disabled={disabled} />
							</ListItemAvatar>
							<ListItemText
								primary={t('pricing.pricingTable', 'Pricing table')}
								secondary={t(
									'pricing.pricingTableDescription',
									'Define pricing by rental duration',
								)}
							/>
						</ListItem>
					</List>
				</>
			)}
			<Collapse in={!displayAsFixedPrice}>
				<EmptyState
					isEmpty={items.length === 0}
					isLoading={false}
					emptyContent={
						<PricingTableEmpty
							onChooseExisting={(tableName) => onUseSavedTable(tableName)}
							onCreateNew={(items) => onTableChange(basePrice, items)}
							disabled={props.disabled}
						/>
					}
					render={() => (
						<>
							<Box
								p={2}
								display="flex"
								flexDirection="row"
								alignItems="center"
								justifyContent="space-between"
							>
								<MoneyTextField
									currency={currency}
									label={t('pricing.basePrice', 'Base price')}
									value={basePrice}
									dataQA="basePrice"
									useMinorUnits
									onValueChange={(newBasePrice) => {
										if (typeof pricingTable === 'string') {
											onBasePriceChange(newBasePrice);
										} else {
											onTableChange(newBasePrice, getItemsWithBasePrice(items, newBasePrice));
										}
									}}
									variant="outlined"
									disabled={props.disabled}
								/>
							</Box>
							{typeof pricingTable === 'string' ? (
								<Box p={2}>
									<Accordion
										key="using-template"
										elevation={0}
										className={classes.expansionPanel}
										disabled={props.disabled}
										sx={{
											display: props.disabled ? 'none' : 'auto',
										}}
									>
										<AccordionSummary aria-controls="panel1a-content" id="panel1a-header">
											<Box
												display="flex"
												flexDirection="row"
												justifyContent="space-between"
												alignItems="flex-start"
												width="100%"
											>
												<Typography className={classes.expansionPanelTitle}>
													<LinkIcon className={classes.expansionPanelIcon} />
													{t('pricing.usingTemplate', 'Using template')}
													{': '}
													{pricingTable}
												</Typography>
												<Box className={classes.expansionPanelExpand}>
													<Typography
														variant="button"
														color="inherit"
														className={classes.expansionPanelExpandText}
													>
														{t('common:actions.options', 'Options')}
													</Typography>
													<ExpandMoreIcon color="inherit" />
												</Box>
											</Box>
										</AccordionSummary>
										<AccordionDetails>
											<List>
												<ListItem
													button
													onClick={() => setEditing(true)}
													disabled={props.disableEditingTemplates}
													className={classes.listItem}
												>
													<ListItemText
														primary={t('pricing.editTemplate', 'Edit template')}
														secondary={t(
															'pricing.editTemplateDescription',
															'Edit the currently selected template',
														)}
													/>
												</ListItem>
												<ListItem
													button
													onClick={() => setIsSelectingTable(true)}
													className={classes.listItem}
												>
													<ListItemText
														primary={t('pricing.changeTemplate', 'Change template')}
														secondary={t(
															'pricing.changeTemplateDescription',
															'Use another one of your saved pricing templates',
														)}
													/>
												</ListItem>
												<ListItem button onClick={handleUnlinkTable} className={classes.listItem}>
													<ListItemText
														primary={t('pricing.unlinkTemplate', 'Unlink from template')}
														secondary={t(
															'pricing.unlinkTemplateDescription',
															'Copy all of the rows in this pricing template and customise the pricing for this product individually',
														)}
													/>
												</ListItem>
											</List>
										</AccordionDetails>
									</Accordion>
								</Box>
							) : (
								<Box p={2}>
									<Accordion
										key="standalone"
										elevation={0}
										className={classes.expansionPanel}
										disabled={props.disabled}
										sx={{
											display: props.disabled ? 'none' : 'auto',
										}}
									>
										<AccordionSummary aria-controls="panel2a-content" id="panel2a-header">
											<Box
												display="flex"
												flexDirection="row"
												justifyContent="space-between"
												alignItems="flex-start"
												width="100%"
											>
												<Typography className={classes.expansionPanelTitle}>
													<LinkOffIcon className={classes.expansionPanelIcon} />{' '}
													{t(
														'pricing.standalonePricingTable',
														'This is a standalone pricing table',
													)}
												</Typography>
												<Box className={classes.expansionPanelExpand}>
													<Typography
														variant="button"
														color="inherit"
														className={classes.expansionPanelExpandText}
													>
														{t('pricing.options', 'Options')}
													</Typography>
													<ExpandMoreIcon color="inherit" />
												</Box>
											</Box>
										</AccordionSummary>
										<AccordionDetails>
											<List>
												<ListItem
													button
													onClick={() => setIsSelectingTable(true)}
													className={classes.listItem}
												>
													<ListItemText
														primary={t('pricing.usePricingTemplate', 'Use a pricing template')}
														secondary={t(
															'pricing.usePricingTemplateDescription',
															'Use one of your existing pricing templates',
														)}
													/>
												</ListItem>
												<ListItem
													button
													onClick={() => setEditing(true)}
													disabled={props.disableEditingTemplates}
													className={classes.listItem}
												>
													<ListItemText
														primary={t('pricing.saveAsNewTemplate', 'Save as new template')}
														secondary={t(
															'pricing.saveAsNewTemplateDescription',
															'Create a new pricing table template from the rows in this table',
														)}
													/>
												</ListItem>
											</List>
										</AccordionDetails>
									</Accordion>
								</Box>
							)}
							<PricingTableBody
								items={items}
								hideMultiplier={typeof pricingTable !== 'string'}
								onChange={(items) => onTableChange(basePrice, items)}
								currency={currency}
								basePrice={basePrice}
								locked={props.disabled ?? (items.length === 0 || typeof pricingTable === 'string')}
							/>
							{isEditing && (
								<PricingTableEditModal
									onClose={() => setEditing(false)}
									onCreateOrUpdateTable={(tableName) => onUseSavedTable(tableName)}
									basePrice={basePrice}
									productId={props.productId}
									items={typeof pricingTable !== 'string' ? items : undefined}
									tableName={typeof pricingTable === 'string' ? pricingTable : undefined}
								/>
							)}
							<PricingTableSelectModal
								isOpen={isSelectingTable}
								onClose={() => setIsSelectingTable(false)}
								onSelect={(tableName) => onUseSavedTable(tableName)}
							/>
						</>
					)}
				/>
			</Collapse>
		</Box>
	);
};

const useStyles = makeStyles((theme: Theme) =>
	createStyles({
		table: {
			border: `2px solid ${theme.palette.border.paper}`,
		},
		tableActions: {
			padding: theme.spacing(2),
			paddingTop: theme.spacing(1),
			display: 'flex',
			flexDirection: 'row',
			flexWrap: 'wrap',
		},
		tableAction: {
			marginLeft: theme.spacing(1),
			marginTop: theme.spacing(1),
		},
		expansionPanel: {
			color: theme.palette.primary.main,
			background: theme.palette.primary.lightest,
			border: `2px solid ${theme.palette.primary.light}`,
		},
		expansionPanelTitle: {
			display: 'flex',
			flexDirection: 'row',
			aligItems: 'center',
		},
		expansionPanelExpand: {
			display: 'flex',
			alignItems: 'center',
			flexDirection: 'row',
			color: theme.palette.primary.main,
			marginLeft: theme.spacing(2),
		},
		expansionPanelExpandText: {
			[theme.breakpoints.down('sm')]: {
				display: 'none',
			},
		},
		expansionPanelIcon: {
			marginRight: theme.spacing(0.5),
		},
		listItem: {
			'&:hover': {
				background: `rgba(0,0,0, ${theme.palette.action.hoverOpacity})`,
			},
		},
	}),
);

export default PricingTable;
